import cn from 'classnames';
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchEntry, searchAttributes } from './search';

type Props = {
  index: SearchEntry[];
  onSelect: (entry: SearchEntry) => void;
};

function SearchAttributes({ index, onSelect }: Props) {
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [focused, setFocused] = React.useState(false);
  const activeResult = React.useRef<HTMLLIElement>(null);

  const { results, total } = React.useMemo(
    () => searchAttributes(index, query),
    [index, query]
  );

  const open = focused && query.trim().length > 0;
  const hidden = total - results.length;

  React.useEffect(
    () => {
      if (activeResult.current) {
        activeResult.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },
    [active, results]
  );

  const reset = () => {
    setQuery('');
    setActive(0);
  };

  const select = (entry: SearchEntry) => {
    if (!entry) return;
    onSelect(entry);
    reset();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      reset();
      return;
    }

    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(results[active]);
    }
  };

  return (
    <div className="editor__attributes-search" data-cy="attributes-search">
      <div className="editor__attributes-search__field">
        <FontAwesomeIcon icon="search" />
        <input
          type="text"
          data-cy="attributes-search-input"
          placeholder={`Search ${index.length} attributes`}
          value={query}
          role="combobox"
          aria-expanded={open}
          aria-controls="attributes-search-results"
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
        />
        {query && (
          <button
            type="button"
            className="editor__attributes-search__clear"
            data-cy="attributes-search-clear"
            aria-label="Clear search"
            onClick={reset}
          >
            <FontAwesomeIcon icon="times" />
          </button>
        )}
      </div>

      {open && (
        <ul
          className="editor__attributes-search__results"
          id="attributes-search-results"
          role="listbox"
          data-cy="attributes-search-results"
        >
          {results.map((entry, position) => (
            <li
              key={entry.key}
              ref={position === active ? activeResult : null}
              role="option"
              aria-selected={position === active}
            >
              <button
                type="button"
                data-cy="attributes-search-result"
                className={cn('editor__attributes-search__result', {
                  'editor__attributes-search__result--active': position === active
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(entry);
                }}
                onMouseEnter={() => setActive(position)}
              >
                <span className="editor__attributes-search__result-name">
                  {entry.label}
                </span>
                <span className="editor__attributes-search__result-section">
                  {entry.sectionTitle}
                </span>
              </button>
            </li>
          ))}

          {results.length === 0 && (
            <li className="editor__attributes-search__empty">
              {`No attribute matches “${query}”`}
            </li>
          )}

          {hidden > 0 && (
            <li className="editor__attributes-search__more">
              {`${hidden} more match${hidden === 1 ? '' : 'es'} — keep typing to narrow`}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchAttributes;
